// v5/post.js: the shell's copy of ../post.js (read-only for the shell), plus a
// NaN/Inf guard: a world that writes a few NaN pixels must not punch black
// squares into the frame or bloom them into white blobs.
// post.js — the film look, in one small pipeline:
// scene -> HDR target -> dual-filter bloom -> tone map, colour grade,
// vignette, grain and dither (no banding in the twilight gradients).
import * as THREE from 'three';
import { FullScreenQuad } from 'three/addons/postprocessing/Pass.js';

const VERT = 'varying vec2 vUv; void main(){ vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }';

// Downsample: 4 diagonal taps + centre. The first level also keeps only
// the bright part (soft knee), so the sky doesn't bloom, only lights do.
const DOWN = /* glsl */`
uniform sampler2D src; uniform vec2 texel; uniform float first; uniform float threshold;
varying vec2 vUv;
bool badf(float v) { return (floatBitsToUint(v) & 0x7f800000u) == 0x7f800000u; }
bool bad3(vec3 c) { return badf(c.r) || badf(c.g) || badf(c.b); }
vec3 fix3(vec3 c) { return bad3(c) ? vec3(0.0) : max(c, vec3(0.0)); } // NaN/Inf and negatives (negative HDR comes out of ACES white)
void main() {
  vec3 c = fix3(texture2D(src, vUv).rgb) * 4.0;
  c += fix3(texture2D(src, vUv + texel * vec2(-1.0, -1.0)).rgb);
  c += fix3(texture2D(src, vUv + texel * vec2( 1.0, -1.0)).rgb);
  c += fix3(texture2D(src, vUv + texel * vec2(-1.0,  1.0)).rgb);
  c += fix3(texture2D(src, vUv + texel * vec2( 1.0,  1.0)).rgb);
  c /= 8.0;
  if (first > 0.5) {
    float br = max(c.r, max(c.g, c.b));
    float knee = threshold * 0.6;
    float soft = clamp(br - threshold + knee, 0.0, 2.0 * knee);
    soft = soft * soft / (4.0 * knee + 1e-5);
    c *= max(soft, br - threshold) / max(br, 1e-5);
    c = min(c, vec3(60.0)); // one blown pixel shouldn't flash the whole frame
  }
  gl_FragColor = vec4(c, 1.0);
}`;

// Upsample the smaller level with a tent filter and add this level's own.
const UP = /* glsl */`
uniform sampler2D src; uniform sampler2D base; uniform vec2 texel; uniform float baseW;
varying vec2 vUv;
void main() {
  vec3 c = texture2D(src, vUv + texel * vec2(-2.0, 0.0)).rgb;
  c += texture2D(src, vUv + texel * vec2( 2.0, 0.0)).rgb;
  c += texture2D(src, vUv + texel * vec2(0.0, -2.0)).rgb;
  c += texture2D(src, vUv + texel * vec2(0.0,  2.0)).rgb;
  c += texture2D(src, vUv + texel * vec2(-1.0, -1.0)).rgb * 2.0;
  c += texture2D(src, vUv + texel * vec2( 1.0, -1.0)).rgb * 2.0;
  c += texture2D(src, vUv + texel * vec2(-1.0,  1.0)).rgb * 2.0;
  c += texture2D(src, vUv + texel * vec2( 1.0,  1.0)).rgb * 2.0;
  gl_FragColor = vec4(c / 12.0 + texture2D(base, vUv).rgb * baseW, 1.0);
}`;

const FINAL = /* glsl */`
uniform sampler2D scene; uniform sampler2D bloom;
uniform float bloomK, exposure, time, grain, vignette, ca, sat, contrast;
uniform vec3 shadows, highs, gainC;
uniform vec2 res;
uniform vec2 uLens; uniform float uThetaE, uLensK; // [gravity] pointer lens (gravity agent, LIGHT.md 4)
varying vec2 vUv;
float hash(vec2 p) { vec3 p3 = fract(vec3(p.xyx) * 0.1031); p3 += dot(p3, p3.yzx + 33.33); return fract((p3.x + p3.y) * p3.z); }
vec3 aces(vec3 x) { return clamp((x * (2.51 * x + 0.03)) / (x * (2.43 * x + 0.59) + 0.14), 0.0, 1.0); }
vec3 toSRGB(vec3 c) { return mix(c * 12.92, 1.055 * pow(c, vec3(1.0 / 2.4)) - 0.055, step(0.0031308, c)); }
bool badf(float v) { return (floatBitsToUint(v) & 0x7f800000u) == 0x7f800000u; }
bool bad3(vec3 c) { return badf(c.r) || badf(c.g) || badf(c.b); }
vec3 fix3(vec3 c) { return bad3(c) ? vec3(0.0) : max(c, vec3(0.0)); } // NaN/Inf and negatives (negative HDR comes out of ACES white)
float safeU(float v, float d, float hi) { return badf(v) ? d : clamp(v, 0.0, hi); }
// a bad pixel is rebuilt from a ring of good neighbours (NaN blocks from upsampled buffers are ~10 px)
vec3 sceneAt(vec2 uv) {
  vec3 c = texture2D(scene, uv).rgb;
  if (!bad3(c)) return c;
  vec3 acc = vec3(0.0); float n = 0.0;
  for (int i = 0; i < 16; i++) {
    float a = float(i) * 0.3927;
    float rr = (i < 8 ? 9.0 : 16.0) + 3.0 * float(i - (i / 2) * 2);
    vec3 s = texture2D(scene, uv + vec2(cos(a), sin(a)) * rr / res).rgb;
    if (!bad3(s)) { acc += s; n += 1.0; }
  }
  return n > 0.0 ? acc / n : vec3(0.0);
}
void main() {
  vec2 d = vUv - 0.5;
  float r2 = dot(d, d);
  // [gravity] the visitor's pointer has mass: a point-mass lens, inverse-mapped per output pixel,
  // beta = theta - thetaE^2 / theta (achromatic). The mass ramps with uLensK; the inner image is clamped
  // on screen and the centre gets a soft shadow (~0.28 thetaE). Uniform early-out: free when uLensK = 0.
  vec2 uv = vUv;
  float shade = 1.0;
  if (uLensK > 0.0) {
    float asp = res.x / max(res.y, 1.0);
    vec2 q = (vUv - uLens) * vec2(asp, 1.0);            // screen-height units
    float th = length(q), tE = uThetaE * sqrt(min(uLensK, 1.0));
    if (th < 6.0 * tE) {
      float t = max(th, 1e-5);
      float w = 1.0 - smoothstep(3.0 * tE, 6.0 * tE, th); // finite reach: seamless, and far pixels pay nothing
      uv = uLens + (q - q * (tE * tE / (t * t)) * w) / vec2(asp, 1.0);
      float rs = 0.28 * tE;
      shade = mix(0.03, 1.0, smoothstep(0.5 * rs, 1.3 * rs, th));
    }
    uv = clamp(uv, 0.5 / res, 1.0 - 0.5 / res);
  }
  vec3 c;
  if (ca > 0.0) {
    vec2 o = d * r2 * ca;
    c = vec3(sceneAt(uv - o).r, sceneAt(uv).g, sceneAt(uv + o).b);
  } else {
    c = sceneAt(uv);
  }
  // guards: a bad grade uniform or a blown bloom level must not wash out the frame
  c += min(fix3(texture2D(bloom, uv).rgb), vec3(256.0)) * safeU(bloomK, 0.8, 4.0);
  c *= shade; // [gravity] lens shadow
  c *= safeU(exposure, 1.0, 16.0) * clamp(fix3(gainC), 0.0, 8.0);
  // negative HDR would come out of ACES as white
  c = aces(max(c, vec3(0.0)));
  // grade: split-tone the shadows and the highlights, then saturation and contrast
  float l = dot(c, vec3(0.2126, 0.7152, 0.0722));
  c += shadows * (1.0 - smoothstep(0.0, 0.25, l)) * 0.035;
  c += highs * smoothstep(0.25, 1.0, l) * 0.12;
  c = mix(vec3(l), c, sat);
  c = max(c, 0.0);
  c = toSRGB(c);
  c = (c - 0.5) * contrast + 0.5;
  // vignette, shaped to the screen
  float v = length(d * vec2(res.x / res.y, 1.0) * 0.9);
  c *= 1.0 - vignette * smoothstep(0.35, 1.05, v);
  // grain: triangular noise, strongest in the mid tones, like film
  float y = dot(c, vec3(0.299, 0.587, 0.114));
  vec2 p = gl_FragCoord.xy + fract(time * 7.13) * vec2(137.0, 71.0);
  float n = hash(p) + hash(p * 1.37 + 11.0) - 1.0;
  c += n * grain * (0.25 + 0.75 * (1.0 - abs(y * 2.0 - 1.0)));
  // dither: kills 8-bit banding in the smooth sky gradients
  c += (hash(gl_FragCoord.xy + 91.7) + hash(gl_FragCoord.xy * 1.61 + 3.3) - 1.0) / 255.0;
  gl_FragColor = vec4(clamp(c, 0.0, 1.0), 1.0);
}`;

function canRenderHalfFloat(renderer) {
  const gl = renderer.getContext();
  if (!gl.getExtension('EXT_color_buffer_float') && !gl.getExtension('EXT_color_buffer_half_float')) return false;
  const rt = new THREE.WebGLRenderTarget(4, 4, { type: THREE.HalfFloatType });
  try {
    renderer.setRenderTarget(rt);
    renderer.clear();
    const ok = gl.checkFramebufferStatus(gl.FRAMEBUFFER) === gl.FRAMEBUFFER_COMPLETE;
    return ok;
  } catch (e) {
    return false;
  } finally {
    renderer.setRenderTarget(null);
    rt.dispose();
  }
}

export class Post {
  constructor(renderer, { levels = 5, msaa = 0 } = {}) {
    this.r = renderer;
    this.half = canRenderHalfFloat(renderer);
    this.type = this.half ? THREE.HalfFloatType : THREE.UnsignedByteType;
    this.levels = levels;
    this.msaa = this.half ? msaa : 0;
    const opts = { type: this.type, depthBuffer: false, minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter };
    this.sceneRT = new THREE.WebGLRenderTarget(1, 1, { type: this.type, samples: this.msaa, minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter });
    this.down = []; this.up = [];
    for (let i = 0; i < levels; i++) { this.down.push(new THREE.WebGLRenderTarget(1, 1, opts)); this.up.push(new THREE.WebGLRenderTarget(1, 1, opts)); }
    this.downQ = new FullScreenQuad(new THREE.ShaderMaterial({ vertexShader: VERT, fragmentShader: DOWN, uniforms: { src: { value: null }, texel: { value: new THREE.Vector2() }, first: { value: 0 }, threshold: { value: this.half ? 0.9 : 0.6 } }, depthTest: false, depthWrite: false }));
    this.upQ = new FullScreenQuad(new THREE.ShaderMaterial({ vertexShader: VERT, fragmentShader: UP, uniforms: { src: { value: null }, base: { value: null }, texel: { value: new THREE.Vector2() }, baseW: { value: 1 } }, depthTest: false, depthWrite: false }));
    this.u = {
      scene: { value: this.sceneRT.texture }, bloom: { value: null }, bloomK: { value: 0.8 }, exposure: { value: 1 }, time: { value: 0 },
      grain: { value: 0.035 }, vignette: { value: 0.35 }, ca: { value: 0.0 }, sat: { value: 1.05 }, contrast: { value: 1.04 },
      shadows: { value: new THREE.Vector3(0.2, 0.3, 0.9) }, highs: { value: new THREE.Vector3(0.0, 0.0, 0.0) }, gainC: { value: new THREE.Vector3(1, 1, 1) },
      res: { value: new THREE.Vector2(1, 1) },
      // [gravity] pointer lens: uv 0..1 (y up), Einstein radius in screen heights, strength 0..1 (0 = off, free)
      uLens: { value: new THREE.Vector2(0.5, 0.5) }, uThetaE: { value: 0.078 }, uLensK: { value: 0 },
    };
    this.finalQ = new FullScreenQuad(new THREE.ShaderMaterial({ vertexShader: VERT, fragmentShader: FINAL, uniforms: this.u, depthTest: false, depthWrite: false }));
  }

  setSize(w, h) {
    w = Math.max(1, Math.floor(w)); h = Math.max(1, Math.floor(h));
    this.sceneRT.setSize(w, h);
    let lw = w, lh = h;
    for (let i = 0; i < this.levels; i++) {
      lw = Math.max(1, lw >> 1); lh = Math.max(1, lh >> 1);
      this.down[i].setSize(lw, lh); this.up[i].setSize(lw, lh);
    }
    this.u.res.value.set(w, h);
  }

  render(scene, camera, before) {
    const r = this.r;
    r.setRenderTarget(this.sceneRT);
    r.clear();
    r.render(scene, camera);
    if (before) before(this.sceneRT);
    this.finish();
  }

  // Worlds and transitions: draw(target) must leave its final HDR (linear)
  // image in `target`. It may use its own offscreen targets first.
  renderWith(draw) {
    const r = this.r;
    r.setRenderTarget(this.sceneRT);
    r.clear();
    draw(this.sceneRT);
    this.finish();
  }

  // Apply a partial grade: { exposure, bloomK, sat, contrast, vignette, grain, ca, threshold, highs:[r,g,b], shadows:[r,g,b], gainC:[r,g,b] }
  setGrade(g) {
    if (!g) return;
    for (const k in g) {
      if (k === 'threshold') { if (Number.isFinite(g[k])) this.downQ.material.uniforms.threshold.value = g[k]; continue; }
      const u = this.u[k];
      if (!u) continue;
      if (Array.isArray(g[k])) { if (g[k].length >= 3 && g[k].every(Number.isFinite)) u.value.set(g[k][0], g[k][1], g[k][2]); }
      else if (Number.isFinite(g[k])) u.value = g[k];
    }
  }

  // [gravity] lens at (x, y) in uv (y up), Einstein radius te in screen heights, strength k (0 = off)
  setLens(x, y, te, k) {
    const u = this.u;
    const ok = Number.isFinite(x) && Number.isFinite(y) && Number.isFinite(te) && te > 0 && Number.isFinite(k);
    u.uLensK.value = ok ? Math.min(1, Math.max(0, k)) : 0;
    if (u.uLensK.value > 0) { u.uLens.value.set(x, y); u.uThetaE.value = Math.min(0.5, te); }
  }

  finish() {
    const r = this.r;
    // bloom chain
    const dm = this.downQ.material.uniforms, um = this.upQ.material.uniforms;
    let src = this.sceneRT;
    for (let i = 0; i < this.levels; i++) {
      dm.src.value = src.texture;
      dm.texel.value.set(1 / src.width, 1 / src.height);
      dm.first.value = i === 0 ? 1 : 0;
      r.setRenderTarget(this.down[i]);
      this.downQ.render(r);
      src = this.down[i];
    }
    let small = this.down[this.levels - 1];
    for (let i = this.levels - 2; i >= 0; i--) {
      um.src.value = small.texture;
      um.base.value = this.down[i].texture;
      um.texel.value.set(0.5 / small.width, 0.5 / small.height);
      um.baseW.value = 1;
      r.setRenderTarget(this.up[i]);
      this.upQ.render(r);
      small = this.up[i];
    }
    this.u.bloom.value = small.texture;
    r.setRenderTarget(null);
    this.finalQ.render(r);
  }
}
